import { sparkline } from '@anytracker/core'

import styles from './Sparkline.module.css'

interface Props {
  /** Recent readings, oldest first. Already quantified by core. */
  readonly values: readonly number[]
  readonly colour: string
  /** Distinguishes this sparkline's gradient from every other one on the page. */
  readonly id: string
}

const WIDTH = 60
const HEIGHT = 24
const INSET = 2

/**
 * A track's last few readings, at thumbnail size (T-907).
 *
 * The arithmetic is core's; this only turns a unit box into pixels. Renders
 * **nothing at all** when there is nothing to draw — a track nobody has logged
 * gets no line rather than a flat one along the bottom, which would read as
 * "recorded, always zero".
 *
 * Hidden from assistive technology. At this size the shape is suggestive rather
 * than readable, and the row states the count in words beside it — which is the
 * form of this information anybody can actually use.
 */
export function Sparkline({ values, colour, id }: Props) {
  const points = sparkline(values)
  if (points === null) {
    return null
  }

  const at = points.map((point) => ({
    x: INSET + point.x * (WIDTH - INSET * 2),
    // SVG y grows downward; the unit box grows up.
    y: HEIGHT - INSET - point.y * (HEIGHT - INSET * 2),
  }))
  const path = at
    .map(
      (point, index) =>
        `${index === 0 ? 'M' : 'L'}${point.x.toFixed(2)} ${point.y.toFixed(2)}`,
    )
    .join(' ')
  /*
   * The same wash as the full chart, so a row and the chart it leads to look
   * like the same drawing at two sizes. No gaps to worry about here: `sparkline`
   * hands back a contiguous run of the most recent readings, not a calendar.
   */
  const fill = `${path} L${at[at.length - 1]!.x.toFixed(2)} ${String(HEIGHT)} L${at[0]!.x.toFixed(2)} ${String(HEIGHT)} Z`
  const fillId = `spark-${id}`

  return (
    <svg
      className={styles['spark']}
      viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
      width={WIDTH}
      height={HEIGHT}
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colour} stopOpacity="0.3" />
          <stop offset="100%" stopColor={colour} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fill} fill={`url(#${fillId})`} stroke="none" />
      <path
        d={path}
        stroke={colour}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
